import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, Menu, Moon, Settings, Sun, User as UserIcon, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCapabilities } from '../../contexts/CapabilitiesContext';
import api from '../../services/api';
import { titleForPath } from './navigation';
import './Layout.css';

const Header = ({ title, onToggleTheme, isDarkMode, onToggleMobileMenu }) => {
  const { user, logout } = useAuth();
  const { isEnabled } = useCapabilities();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const isInAdminArea = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const notificationsOn = isEnabled('Notifications');

  const today = new Date();
  const formattedDate = today.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' });

  // O cabeçalho diz ONDE se está (o nome da página); a saudação fica no corpo do Dashboard. Antes repetia "Olá, <nome>!" em todos os ecrãs.
  const pageTitle = title || titleForPath(location.pathname);
  const firstName = user?.name?.split(' ')[0] || 'Utilizador';
  const heading = pageTitle || `Olá, ${firstName}!`;

  useEffect(() => {
    if (!notificationsOn) { setUnreadCount(0); return; }
    api.get('/notifications/unread-count')
      .then((res) => setUnreadCount(res.data?.count || 0))
      .catch(() => setUnreadCount(0));
  }, [notificationsOn]);

  const go = (path) => { setIsUserMenuOpen(false); navigate(path); };

  const handleSwitchArea = () => {
    if (user?.role !== 'Admin') return;
    go(isInAdminArea ? '/' : '/admin');
  };

  const handleLogoutClick = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  // Close user menu on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!isUserMenuOpen) return;
      const el = userMenuRef.current;
      if (el && !el.contains(e.target)) setIsUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [isUserMenuOpen]);

  return (
    <header className="main-header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onToggleMobileMenu} aria-label="Abrir o menu"><Menu size={22} aria-hidden="true" /></button>
        <div className="header-greeting">
          <h1 data-testid="page-title">{heading}</h1>
          <p className="header-date">{formattedDate}</p>
        </div>
      </div>

      <div className="header-right">
        <button
          type="button"
          className="theme-toggle"
          onClick={onToggleTheme}
          title={isDarkMode ? 'Modo claro' : 'Modo escuro'}
          aria-label={isDarkMode ? 'Mudar para o modo claro' : 'Mudar para o modo escuro'}
        >
          {isDarkMode ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
        </button>

        {notificationsOn && (
          <button type="button" className="notification-btn" onClick={() => go('/notifications')} aria-label={unreadCount > 0 ? `Notificações (${unreadCount} por ler)` : 'Notificações'}>
            <Bell size={18} aria-hidden="true" />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </button>
        )}

        <div className="user-profile-wrapper" ref={userMenuRef}>
          <div
            className="user-profile"
            onClick={() => setIsUserMenuOpen((v) => !v)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsUserMenuOpen((v) => !v); }
              if (e.key === 'Escape') setIsUserMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
          >
            {user?.avatar ? (
              <img src={user.avatar} alt="Avatar" className="user-avatar-img" />
            ) : (
              <div className="user-avatar">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</div>
            )}
            <div className="user-info">
              <span className="user-name">{user?.name || 'Utilizador'}</span>
              <span className="user-role">{user?.role === 'Admin' ? 'Administrador' : (user?.role === 'Trainer' || user?.role === 'PersonalTrainer') ? 'Treinador' : 'Atleta'}</span>
            </div>
            <ChevronDown size={14} className="user-menu-caret" aria-hidden="true" />
          </div>

          {isUserMenuOpen && (
            <div className="user-menu" role="menu">
              {user?.role === 'Admin' && (
                <button type="button" className="user-menu-item" onClick={handleSwitchArea} role="menuitem">
                  {isInAdminArea ? <><UserIcon size={16} aria-hidden="true" /> Área do atleta</> : <><Wrench size={16} aria-hidden="true" /> Painel de administração</>}
                </button>
              )}
              <button type="button" className="user-menu-item" onClick={() => go('/profile')} role="menuitem">
                <UserIcon size={16} aria-hidden="true" /> O meu perfil
              </button>
              {user?.role === 'Admin' && (
                <button type="button" className="user-menu-item" onClick={() => go('/admin/settings')} role="menuitem">
                  <Settings size={16} aria-hidden="true" /> Configurações
                </button>
              )}
              <div className="user-menu-sep" />
              <button type="button" className="user-menu-item danger" onClick={handleLogoutClick} role="menuitem">
                <LogOut size={16} aria-hidden="true" /> Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
